import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { HostSighting } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { ErrorBanner, Spinner } from './common'
import { DataTable } from './DataTable'

/** Una riga leggibile dei fatti del filo di un host. */
export function riassuntoFilo(r: HostSighting): string[] {
  const parti: string[] = []
  if (r.smb_os) parti.push(`${r.smb_os}${r.smb_domain ? ` · ${r.smb_domain}` : ''}`)
  if (r.dicom_ae.length) parti.push(`DICOM ${r.dicom_ae.join('/')}${r.dicom_impl ? ` (${r.dicom_impl})` : ''}`)
  if (r.hl7_apps.length) parti.push(`HL7 ${r.hl7_apps.join('/')}${r.hl7_facility ? ` @ ${r.hl7_facility}` : ''}`)
  if (r.ssh_banner) parti.push(r.ssh_banner)
  if (r.user_agents.length) parti.push(`UA: ${r.user_agents.join(' | ')}`)
  if (r.http_servers.length) parti.push(`Server: ${r.http_servers.join(' | ')}`)
  if (r.cert_subject) parti.push(`${t('certificato')}: ${r.cert_subject}${r.cert_issuer && r.cert_issuer !== r.cert_subject ? ` (${r.cert_issuer})` : ''}`)
  if (r.tls_names.length) parti.push(`SNI: ${r.tls_names.join(', ')}`)
  if (r.http_hosts.length) parti.push(`Host: ${r.http_hosts.join(', ')}`)
  if (r.sip_user_agent) parti.push(`SIP: ${r.sip_user_agent}`)
  if (r.wsd_types.length) parti.push(`WS-Discovery: ${r.wsd_types.join(', ')}`)
  return parti
}

/**
 * La lista degli host con fatti dei protocolli sul filo (HTTP, TLS, SSH,
 * SMB, DICOM, HL7, SIP, WS-Discovery), dietro i numeri di Traffic Analysis.
 * Stessa forma delle altre finestre; da ogni riga si va al CI che ha quell'IP.
 */
export function ElencoHostFilo({ titolo, protocol, onChiudi }: { titolo: string; protocol: string; onChiudi: () => void }) {
  const dati = useAsync(() => api.trafficAnalysisHosts(protocol), [protocol])
  const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString(localeCorrente()) : '—')

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={titolo}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{titolo}</h3>
            <p className="dob-modal__sotto">{dati.data ? t('{n} host', { n: dati.data.count }) : t('Caricamento…')}</p>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        <ErrorBanner error={dati.error} />
        {dati.loading && <Spinner />}
        {dati.data && (
          <div className="dob-modal__corpo">
            <DataTable
              rows={dati.data.rows}
              rowKey={(r) => r.ip}
              conteggio={{ nome: t('host') }}
              emptyText={t('Nessun host per questo filtro.')}
              columns={[
                {
                  key: 'ip', header: 'IP', mono: true, value: (r) => r.ip,
                  render: (r) => <>{r.ip}{r.vlan != null && <small className="text-muted"> VLAN {r.vlan}</small>}</>,
                },
                { key: 'hostname', header: t('Nome') },
                {
                  key: 'kind', header: t('Tipo'), value: (r) => [r.kind, r.medical ? t('medicale') : null].filter(Boolean).join(' '),
                  render: (r) => (
                    <>
                      {r.kind ?? <span className="text-muted">—</span>}
                      {r.medical && <> <span className="badge badge--warn">{t('medicale')}</span></>}
                    </>
                  ),
                },
                {
                  key: 'ci', header: t('Oggetto'), value: (r) => r.ci_name ?? t('non in CMDB'),
                  render: (r) => (r.ci_id
                    ? <Link to={`/cis/${r.ci_id}`} title={r.ci_class ?? undefined}>{r.ci_name}</Link>
                    : <span className="text-muted">{t('non in CMDB')}</span>),
                },
                {
                  key: 'facts', header: t('Cosa ha detto sul filo'), value: (r) => riassuntoFilo(r).join(' · '),
                  render: (r) => riassuntoFilo(r).map((p, i) => <div key={i}><small>{p}</small></div>),
                },
                {
                  key: 'last_seen', header: t('Ultima vista'), type: 'date',
                  render: (r) => <small>{quando(r.last_seen)}</small>,
                },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  )
}
